/**
 * Lightweight wrapper around the File System Access API. Lets the user
 * pick a "recordings folder" once and then have every subsequent
 * recording saved straight to disk under a generated filename.
 *
 * Falls back to a regular download when the API is unavailable.
 */

const DB = 'bodynodelink-fs';
const STORE = 'handles';
const KEY = 'recordings-dir';

// Minimal subset of the File System Access API types we use.
interface FSWritableStream extends WritableStream {
  write(data: Blob | ArrayBuffer | Uint8Array | string): Promise<void>;
  close(): Promise<void>;
}
interface FSFileHandle {
  createWritable(): Promise<FSWritableStream>;
}
interface FSDirectoryHandle {
  getFileHandle(name: string, opts?: { create?: boolean }): Promise<FSFileHandle>;
  queryPermission?(opts: { mode: 'read' | 'readwrite' }): Promise<PermissionState>;
  requestPermission?(opts: { mode: 'read' | 'readwrite' }): Promise<PermissionState>;
}

declare global {
  interface Window {
    showDirectoryPicker?: (opts?: { mode?: 'read' | 'readwrite' }) => Promise<FSDirectoryHandle>;
  }
}

export class FileSystemSaver {
  static isSupported(): boolean {
    return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
  }

  private async openDb(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  private async getStored(): Promise<FSDirectoryHandle | null> {
    const db = await this.openDb();
    const handle = await new Promise<FSDirectoryHandle | null>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(KEY);
      req.onsuccess = () => resolve((req.result as FSDirectoryHandle | undefined) ?? null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return handle;
  }

  private async setStored(handle: FSDirectoryHandle): Promise<void> {
    const db = await this.openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(handle as unknown as IDBValidKey, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  }

  /** Prompt once for a directory and remember it. */
  async pickDirectory(): Promise<boolean> {
    if (!FileSystemSaver.isSupported() || !window.showDirectoryPicker) return false;
    try {
      const handle = await window.showDirectoryPicker({ mode: 'readwrite' });
      await this.setStored(handle);
      return true;
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        console.warn('[FileSystemSaver] picker', err);
      }
      return false;
    }
  }

  /** Returns true if a directory handle is saved AND granted. */
  async hasUsableDirectory(): Promise<boolean> {
    if (!FileSystemSaver.isSupported()) return false;
    const handle = await this.getStored();
    if (!handle) return false;
    if (typeof handle.queryPermission === 'function') {
      const state = await handle.queryPermission({ mode: 'readwrite' });
      if (state === 'granted') return true;
      if (state === 'prompt' && typeof handle.requestPermission === 'function') {
        const again = await handle.requestPermission({ mode: 'readwrite' });
        return again === 'granted';
      }
      return false;
    }
    return true;
  }

  /** Save blob into the chosen directory. Returns true on success. */
  async save(blob: Blob, filename: string): Promise<boolean> {
    if (!(await this.hasUsableDirectory())) return false;
    const handle = await this.getStored();
    if (!handle) return false;
    try {
      const file = await handle.getFileHandle(filename, { create: true });
      const stream = await file.createWritable();
      await stream.write(blob);
      await stream.close();
      return true;
    } catch (err) {
      console.warn('[FileSystemSaver] save', err);
      return false;
    }
  }

  async forget(): Promise<void> {
    const db = await this.openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  }
}
